import { Placement } from '@noths/polaris-client-ribbons-design-system';
import { getProductIdFromProductCode, handleFetchResponse } from '@noths/polaris-client-utils';
import { createAsyncThunk } from '@reduxjs/toolkit';
import fetch from 'isomorphic-fetch';
import getConfig from 'next/config';

import {
  NextConfig,
  ProductPlacementRecord,
  ProductPlacementRecordError,
  SimilarProduct,
} from 'src/types';
import { logger } from 'src/utils';

const { endpoints } = (getConfig() as NextConfig).publicRuntimeConfig;

type ProductFromResponse = Omit<SimilarProduct, 'linkURL'>;

const decorateProductsWithRRData = (
  products: ProductFromResponse[],
  placementItems: RichRelevancePlacementItem[],
): SimilarProduct[] =>
  products.map((product) => {
    const placementItem = placementItems.find(
      ({ id }) => getProductIdFromProductCode(product.code) === Number(id),
    );

    return {
      ...product,
      linkURL: placementItem?.linkURL ?? '',
    };
  });

export const requestSimilarProducts = createAsyncThunk<
  ProductPlacementRecord | null,
  RichRelevancePlacement,
  {
    rejectValue: ProductPlacementRecordError;
  }
>(
  'richRelevance/requestSimilarProducts',
  async ({ placement_name, items = [], message, strategy }, thunkApi) => {
    const ids = items.map((p) => p.id).join('&ids[]=');

    try {
      const apiResponse = await fetch(`${endpoints.recommendedProducts}?ids[]=${ids}`);

      const { products } = ((await handleFetchResponse(apiResponse)) || {}) as Record<
        'products',
        ProductFromResponse[]
      >;

      const similarProducts = decorateProductsWithRRData(products, items);

      return {
        products: similarProducts,
        placementType: placement_name as Placement,
        placementTitle: message,
        placementStrategy: strategy,
      };
    } catch (error) {
      logger.error({
        location: 'requestSimilarProducts',
        extra: { description: `Exception thrown calling handleFetchResponse.`, error },
      });

      return thunkApi.rejectWithValue({
        error: { message: error.message, stack: error.stack, name: error.name },
        placementType: placement_name,
      } as ProductPlacementRecordError);
    }
  },
);
